/**
 * MenuLogin component.
 */
import Button from '@components/atoms/buttons/menuLoginButton';
import Container from '@components/atoms/container/menuLoginContainer';
import Context from '@shared/components/utils/context/menuContext';
import Focus from '@shared/types/enums/focus';
import TranslationManager from '@shared/assets/locale/translationsManager';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { DURATION } from '../../../../shared/types/constants/animations';
import { selectFocus, setFocus } from '@shared/store/slices/focus.slice';
import { loginAction } from '@shared/actions/flow';
import { selectIsAnonymous } from '@shared/store/slices/user.slice';
import { updateParams } from '@shared/store/slices/router.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import {
  selectMenuCollapse,
  selectMenuDisplay,
} from '@shared/store/slices/menu.slice';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Displays the login button in the menu.
 * @returns {JSX.Element} - Menu login component.
 */
export default function MenuLogin() {
  const { showLogin, props, text } = useMenuLogin();
  return showLogin ? (
    <Container {...props.container}>
      <Button {...props.button}>{text}</Button>
    </Container>
  ) : null;
}

/**
 * Hook to manage the menu login component.
 * Manages the display of the login button and the login action.
 * @returns {Object} - Object with the props and text for the menu login component.
 */
function useMenuLogin() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Context
  const { effectiveIndex, setListIndex } = useContext(Context);

  // Selectors
  const collapse = useAppSelector(selectMenuCollapse);
  const menuDisplay = useAppSelector(selectMenuDisplay);
  const focus = useAppSelector(selectFocus);
  const isAnonymous = useAppSelector(selectIsAnonymous);

  // State
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Refs
  const displayRef = useRef(menuDisplay);
  displayRef.current = menuDisplay;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const goToLogin = useCallback(() => {
    effectiveIndex.current = 0;
    setListIndex.current(0);
    setFocused(false);
    setHovered(false);
    setTimeout(() => {
      loginAction();
    });
  }, []);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onClick = useCallback(() => {
    if (!displayRef.current) return;
    goToLogin();
  }, []);

  const onPointerEnter = useCallback(() => {
    if (!displayRef.current) return;
    setHovered(true);
  }, []);

  const onPointerLeave = useCallback(() => {
    if (!displayRef.current) return;
    setHovered(false);
  }, []);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  useEffect(() => {
    setFocused(focus === Focus.LOGIN);
  }, [focus]);

  // ------------------------------------------------------------
  // Keyboard hook
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.LOGIN,
    keyDownHandler: (key) => {
      switch (key) {
        case 'LEFT':
        case 'DOWN':
          dispatch(setFocus(Focus.SEARCH));
          break;
        case 'SELECT':
          dispatch(updateParams({ focus: Focus.MENU }));
          loginAction();
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      showLogin: !collapse && isAnonymous,
      props: {
        button: {
          focused: focused || hovered,
          onClick,
          onPointerEnter,
          onPointerLeave,
        },
        container: {
          duration: DURATION.MENU_LOGIN,
          focused: focused || hovered,
        },
      },
      text: TranslationManager.get('COMPONENTS.LOGIN'),
    };
  }, [
    collapse,
    focused,
    hovered,
    isAnonymous,
    onClick,
    onPointerEnter,
    onPointerLeave,
  ]);
}
